import * as React from 'react'

import { ChevronUpIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import IntervalEditor, {
  IIntervalEditorResult,
  NextDayLabel,
} from '~/client/src/shared/components/IntervalEditor'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { doesWindowEndNextDay } from '~/client/src/shared/utils/projectSchedule'

interface IProps {
  startTime: number
  endTime: number
  projectDateStore: ProjectDateStore
  onChange: (startDate: Date, endDate: Date) => void
  ariaLabel: string
}

/**
 * The window a day is worked, shown as a select and edited in a popover. The
 * editor is the same one Equipment controls use, in its time-only mode — a
 * project's schedule has no dated variant.
 */
function TimeRangeField({
  startTime,
  endTime,
  projectDateStore,
  onChange,
  ariaLabel,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  const handleSave = (result: IIntervalEditorResult) => {
    onChange(new Date(result.startDate), new Date(result.endDate))
    setIsOpen(false)
  }

  const endsNextDay = doesWindowEndNextDay(
    projectDateStore.getDaySeconds(startTime),
    projectDateStore.getDaySeconds(endTime),
  )

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        aria-label={ariaLabel}
        render={<FieldTrigger icon={isOpen ? <ChevronUpIcon /> : undefined} />}
      >
        {projectDateStore.getTimeIntervalPerDayToDisplay(startTime, endTime)}
        {endsNextDay && <NextDayLabel />}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="tw:w-[320px] tw:p-0"
        // Opening the row with the pointer should not drop the caret into Start
        // time and select its text — the window is there to be read, and a
        // stray keystroke would overwrite it. Opening from the keyboard still
        // moves focus inside, or there would be no way to reach the fields.
        initialFocus={openType => openType === 'keyboard'}
      >
        {/* Keyed so reopening the row re-seeds the editor from the saved window
            rather than the draft the user abandoned last time. */}
        <IntervalEditor
          key={`${startTime}-${endTime}`}
          isDailyOnly={true}
          initial={{ startDate: startTime, endDate: endTime, isDaily: true }}
          projectDateStore={projectDateStore}
          onSave={handleSave}
          onCancel={() => setIsOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}

export default observer(TimeRangeField)
